/**
 * Pure-function display helpers for notifications. Kept separate from the
 * `NOTIFICATIONS_STORE` because they're stateless — extracting them lets
 * components import them without pulling in the full store.
 */
import {
  Notification} from '@shared/definitions/notification.interface';
import {
  NotificationPriority,
  NotificationType
} from '@shared/definitions/notification.interface';

/**
 * Icon name for a notification. Honours per-instance icon override.
 *
 * The name returned is a Material LIGATURE — the notification list renders it
 * as `<mat-icon>{{ name }}</mat-icon>`, not as `svgIcon`. Producers that copied
 * the navigation convention wrote `mat:support_agent`, and the font drew that
 * string literally, giving the garbled glyph in the bell. The prefix is
 * stripped here rather than backfilled: the notifications already written to
 * Firestore carry it forever, and this repairs them on read.
 */
export function getNotificationIcon(notification: Notification): string {
  if (notification.icon) {
    return stripIconRegistryPrefix(notification.icon);
  }

  switch (notification.type) {
    case NotificationType.SUPPORT_REPLY:
      return 'support_agent';
    case NotificationType.PAYMENT_RECEIVED:
      return 'payments';
    case NotificationType.PAYMENT_OVERDUE:
    case NotificationType.PAYMENT_DUE_SOON:
      return 'schedule';
    case NotificationType.CONTRACT_EXPIRING:
    case NotificationType.CONTRACT_EXPIRED:
      return 'description';
    case NotificationType.INDEXATION_DUE:
    case NotificationType.INDEXATION_READY:
      return 'trending_up';
    case NotificationType.MAINTENANCE_SCHEDULED:
    case NotificationType.MAINTENANCE_OVERDUE:
      return 'build';
    case NotificationType.DOCUMENT_READY:
      return 'folder';
    case NotificationType.SYSTEM_UPDATE:
      return 'system_update';
    case NotificationType.WELCOME:
      return 'waving_hand';
    case NotificationType.TEST:
      return 'bug_report';
    case NotificationType.ACTION_CARD_CRITICAL:
      return 'priority_high';
    default:
      return 'notifications';
  }
}

/**
 * Drop the `mat:` (or any `<set>:`) registry prefix from an icon name.
 *
 * `mat:foo` addresses the SVG sprite registered by Vex and only works through
 * `[svgIcon]`. Anywhere the name is used as a ligature it has to be bare.
 */
function stripIconRegistryPrefix(icon: string): string {
  const separator = icon.indexOf(':');
  return separator === -1 ? icon : icon.slice(separator + 1);
}

/** Material theme palette for a notification based on its priority. */
export function getNotificationColor(notification: Notification): string {
  switch (notification.priority) {
    case NotificationPriority.URGENT:
      return 'warn';
    case NotificationPriority.HIGH:
      return 'accent';
    case NotificationPriority.NORMAL:
      return 'primary';
    case NotificationPriority.LOW:
      return '';
    default:
      return 'primary';
  }
}
